// owner/shell/OwnerShell.js — the owner experience's frame, on a phone and on a computer
// (signed-off mock-ups: docs/02_Product/design_system/owner-app-mockups/).
//   phone:    header with the logo, the page, a bottom tab bar (overview · bookings · units · finance · more);
//             «المزيد» opens a sheet with settings and sign-out.
//   computer: one top bar with all five destinations and sign-out — no sidebar.
// Guests and guest requests left the owner experience in phase 1 (§10 "8c batch 3").
// Used by the store app (owner/app/OwnerApp.js) and, from the end of 8c, by owners on the web.

import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import useLanguage from '../../hooks/useLanguage';
import Icon from '../ui/Icon';
import useOwnerSignOut from './useOwnerSignOut';
import logo from '../assets/hostme-logo.png';
import '../ui/owner.css';

export const OWNER_NAV = [
  { to: '/dashboard', key: 'overview', icon: 'house', tab: true },
  { to: '/bookings', key: 'bookings', icon: 'calendar', tab: true },
  { to: '/properties', key: 'units', icon: 'units', tab: true },
  { to: '/finance', key: 'finance', icon: 'wallet', tab: true },
  { to: '/settings', key: 'settings', icon: 'gear' }
];

/** The house shape used to clip unit thumbnails (objectBoundingBox, so it fits any size). */
const HouseClip = () => (
  <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true" focusable="false">
    <clipPath id="hm-house-clip" clipPathUnits="objectBoundingBox">
      <path d="M0.42 0.05 L0.07 0.30 Q0 0.35 0 0.44 L0 0.88 Q0 1 0.12 1 L0.88 1 Q1 1 1 0.88 L1 0.44 Q1 0.35 0.93 0.30 L0.58 0.05 Q0.5 -0.01 0.42 0.05 Z" />
    </clipPath>
  </svg>
);

const navClass = ({ isActive }) => (isActive ? 'active' : undefined);

const OwnerShell = () => {
  const { t, isArabic } = useLanguage();
  const signOut = useOwnerSignOut();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMoreOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [moreOpen]);

  const moreActive = OWNER_NAV.some((item) => !item.tab && location.pathname.startsWith(item.to));

  return (
    <div className="hm-owner" dir={isArabic ? 'rtl' : 'ltr'} lang={isArabic ? 'ar' : 'en'}>
      <HouseClip />
      <div className="o-shell">
        <header className="o-topnav">
          <NavLink to="/dashboard" className="o-brand" aria-label={t('ownerApp.nav.home')}>
            <img src={logo} alt="Host Me هوست مي" />
          </NavLink>
          <nav aria-label={t('ownerApp.nav.label')}>
            {OWNER_NAV.map((item) => (
              <NavLink key={item.to} to={item.to} className={navClass}>{t(`ownerApp.nav.${item.key}`)}</NavLink>
            ))}
          </nav>
          <div className="o-end">
            <button type="button" className="o-btn o-btn-ghost" onClick={() => signOut()}>
              <Icon name="logout" size="sm" />{t('ownerApp.nav.signOut')}
            </button>
          </div>
        </header>

        <header className="o-header">
          <NavLink to="/dashboard" className="o-brand" aria-label={t('ownerApp.nav.home')}>
            <img src={logo} alt="Host Me هوست مي" />
          </NavLink>
        </header>

        <main className="o-content">
          <Outlet />
        </main>
      </div>

      <nav className="o-tabbar" aria-label={t('ownerApp.nav.label')}>
        {OWNER_NAV.filter((item) => item.tab).map((item) => (
          <NavLink key={item.to} to={item.to} className={({ isActive }) => `o-tab${isActive ? ' active' : ''}`}>
            <Icon name={item.icon} />
            <span>{t(`ownerApp.nav.${item.key}Tab`)}</span>
          </NavLink>
        ))}
        <button
          type="button"
          className={`o-tab${moreActive || moreOpen ? ' active' : ''}`}
          aria-expanded={moreOpen}
          aria-controls="o-more-sheet"
          onClick={() => setMoreOpen(true)}
        >
          <Icon name="more" />
          <span>{t('ownerApp.nav.moreTab')}</span>
        </button>
      </nav>

      {moreOpen && (
        <>
          <div className="o-scrim" onClick={() => setMoreOpen(false)} aria-hidden="true" />
          <div className="o-more-sheet" id="o-more-sheet" role="dialog" aria-modal="true" aria-label={t('ownerApp.nav.moreTab')}>
            <div className="o-grip" />
            {OWNER_NAV.filter((item) => !item.tab).map((item) => (
              <NavLink key={item.to} to={item.to}>
                <Icon name={item.icon} />{t(`ownerApp.nav.${item.key}`)}
              </NavLink>
            ))}
            <button type="button" className="o-signout" onClick={() => signOut()}>
              <Icon name="logout" />{t('ownerApp.nav.signOut')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default OwnerShell;
